// Global Instructions Rule Applied!
//
// Keys inside the per-user global (non-report) preference blob
// (public.user_preferences_app.data), plus the readers that validate them.
//
// Sibling of tableViewPreferences.ts, which owns the equivalent blob keys for
// REPORT-scoped preferences in user_preferences_reports.

/** Left-nav current area. Value is a cfg.menu_sections.key. */
export const PREF_APP_CURRENT_AREA = 'current_area'

/** Agent surface sections: per-surface collapsed flags. Value is a map of
 *  surface key → boolean, e.g.
 *  { company_dashboard: true, lp_dashboard_drafts: false, agent_tab_signals: true }.
 *
 *  It began as the states strips alone (Option A, owner 2026-08-25 —
 *  collapsed = the compact pill row, expanded = the card row) and covers the
 *  Agent Drafts and Agent Signals sections too since 2026-09-06, through the
 *  shared core/hooks/useSectionCollapse. Keys are free-form, so a new section
 *  needs no migration — but the STORED KEY stays 'agent_states_collapsed'
 *  whatever it grows to cover: renaming it would silently discard every user's
 *  saved positions.
 *
 *  Each caller supplies its own fallback for a surface the user has not folded
 *  yet: the company and deal strips start collapsed, the prospect and LP
 *  strips start open, and drafts and signals start open (they are the action
 *  and attention layers). A stored choice always wins. */
export const PREF_APP_AGENT_STATES_COLLAPSED = 'agent_states_collapsed'

/** Deal Pipeline → Budget tab: the funds the user follows there, as fund ids.
 *  Absent = the cfg default (app_config deal_pipeline_budget / default_funds,
 *  resolved by the read itself), so a user who never picked sees the firm's
 *  choice and a user who did sees their own. */
export const PREF_APP_BUDGET_FUND_IDS = 'deal_pipeline_budget_fund_ids'

/** The stored Budget tab fund pick, or null when the user has not made one.
 *  A stored value that is not a list of strings counts as absent. */
export function readBudgetFundIds(globalConfigs: AppPreferenceSource | null | undefined): string[] | null {
  const stored = globalConfigs?.user_preferences_app?.data?.[PREF_APP_BUDGET_FUND_IDS]
  if (!Array.isArray(stored)) return null
  const ids = stored.filter((v): v is string => typeof v === 'string' && v.length > 0)
  return ids
}

export function readAgentStatesCollapsed(
  globalConfigs: AppPreferenceSource | null | undefined,
  surfaceKey: string,
  // The surface's own starting position when the user has not chosen one
  // (company / deal strips start collapsed; prospect and LP strips, drafts and
  // signals start open).
  fallback: boolean = true
): boolean {
  const stored = globalConfigs?.user_preferences_app?.data?.[PREF_APP_AGENT_STATES_COLLAPSED]
  if (typeof stored !== 'object' || stored == null) return fallback
  const value = (stored as Record<string, unknown>)[surfaceKey]
  return typeof value === 'boolean' ? value : fallback
}

interface AppPreferenceSource {
  user_preferences_app?: { data?: Record<string, unknown> } | null
  menu_sections?: Array<{ key: string }>
}

/**
 * The stored left-nav area, or null when absent or no longer valid.
 *
 * Validity is MEMBERSHIP in the delivered menu_sections — one check that covers
 * every stale case at once, because get_global_configs_v3 already filters on
 * is_visible server-side. A section that was hidden, deleted or renamed is
 * simply absent from the array, so the stored key fails the test and the caller
 * falls back to the default area.
 */
export function readStoredAreaKey(globalConfigs: AppPreferenceSource | null | undefined): string | null {
  const stored = globalConfigs?.user_preferences_app?.data?.[PREF_APP_CURRENT_AREA]
  if (typeof stored !== 'string' || !stored) return null
  const sections = globalConfigs?.menu_sections
  if (!sections?.length) return null
  return sections.some((s) => s.key === stored) ? stored : null
}
